import { v4 } from 'uuid';

import { bindCallbacks, statusText } from 'scripts/infrastructure/backends/fake_backend_http/lib/common';

/**
 * Deterministic responses that cycle through different types:
 * 0: info, plain text, no citations
 * 1: suggested_response, HTML, 2 citations
 * 2: info, rich HTML list, 1 citation, review reasons
 * 3: suggested_response, plain text, no citations
 */
const RESPONSES = [
  {
    content:
      'This customer has been with us since 2019 and has 12 previous conversations. Their most recent interaction was about an order status inquiry.',
    citations: [],
    confidence: 0.95,
    reviewReasons: [],
    type: 'info',
  },
  {
    content:
      '<p>Thank you for reaching out! I can see your order <strong>#1234</strong> is currently in transit and expected to arrive by Friday.</p><p>Is there anything else I can help you with?</p>',
    citations: [
      { snippetId: 'snippet-austen-id', snippetName: 'Austen snippet' },
      { snippetId: 'snippet-follow-id', snippetName: 'Follow' },
    ],
    confidence: 0.88,
    reviewReasons: [],
    type: 'suggested_response',
  },
  {
    content:
      '<p>Here is the customer summary:</p><ul><li><strong>Name:</strong> Mark Jefferson</li><li><strong>Loyalty tier:</strong> Gold</li><li><strong>Last purchase:</strong> January 2025</li></ul>',
    citations: [{ snippetId: 'snippet-austen-id', snippetName: 'Austen snippet' }],
    confidence: 0.72,
    reviewReasons: ['Response may contain outdated information', 'Low confidence score'],
    type: 'info',
  },
  {
    content:
      'I apologize for the inconvenience. Let me look into this issue for you right away and get back to you with an update.',
    citations: [],
    confidence: 0.91,
    reviewReasons: [],
    type: 'suggested_response',
  },
];

const CANNED_RESPONSES = [
  {
    label: 'Summarize this customer',
    message: 'Give me a summary of this customer including their history and key details',
  },
  {
    label: 'Draft a response',
    message: "Draft a response to the customer's most recent message",
    alwaysShow: true,
  },
];

export default class TeamAssistService {
  constructor(database, pubsub) {
    this.getDatabase = database;
    this._pubsub = pubsub;
    // Per-customer session state: { customerId: { messages: [], responseIndex: 0, sessionId } }
    this._sessions = {};
  }

  fetchAll(attrs, callback, url, { orgId, agentId, customerId }) {
    const session = this._sessions[customerId];
    const messages = session ? session.messages : [];
    callback(null, { status: 200, statusText: statusText(200), response: messages });
  }

  ask({ id, question, displayedMessage }, callback, url, { orgId, agentId, customerId }) {
    if (question === 'return an error') {
      callback(null, { status: 500, statusText: statusText(500), response: { error: 'Simulated error' } });
      return;
    }

    const session = this._getOrCreateSession(customerId);

    const questionMsg = {
      id,
      agentId,
      content: question,
      displayedMessage: displayedMessage || undefined,
      citations: [],
      confidence: 0,
      createdAt: new Date().toISOString(),
      customerId,
      reviewReasons: [],
      role: 'agent_question',
      sessionId: session.sessionId,
      type: 'question',
    };

    const template = RESPONSES[session.responseIndex % RESPONSES.length];
    session.responseIndex++;

    const responseMsg = {
      id: v4(),
      agentId,
      content: template.content,
      citations: template.citations,
      confidence: template.confidence,
      createdAt: new Date().toISOString(),
      customerId,
      reviewReasons: template.reviewReasons,
      role: 'ai_response',
      sessionId: session.sessionId,
      type: template.type,
    };

    session.messages.push(questionMsg, responseMsg);

    // Mirror the production backend: the HTTP POST acknowledges the question
    // synchronously, and the AI response is delivered asynchronously over
    // MQTT broadcast.
    callback(null, {
      status: 200,
      statusText: statusText(200),
      response: { messages: [questionMsg] },
    });

    this._pubsub.publish(`v1/orgs/${orgId}/agents/${agentId}/customers/${customerId}/team-assists/${responseMsg.id}`, {
      payload: responseMsg,
    });
  }

  retry(attrs, callback, url, { orgId, agentId, customerId, messageId }) {
    const session = this._sessions[customerId];
    if (!session) {
      callback(null, { status: 404, statusText: statusText(404) });
      return;
    }

    const retried = {
      id: messageId,
      agentId,
      content: '<p>Here is a revised response after retry.</p>',
      citations: [],
      confidence: 0.93,
      createdAt: new Date().toISOString(),
      customerId,
      reviewReasons: [],
      role: 'ai_response',
      sessionId: session.sessionId,
      type: 'suggested_response',
    };

    const idx = session.messages.findIndex(m => m.id === messageId);
    if (idx >= 0) {
      session.messages[idx] = retried;
    }

    callback(null, { status: 200, statusText: statusText(200), response: retried });
  }

  updateMessage({ rating }, callback, url, { orgId, agentId, customerId, teamAssistId }) {
    const session = this._sessions[customerId];
    if (!session) {
      callback(null, { status: 404, statusText: statusText(404) });
      return;
    }

    const idx = session.messages.findIndex(m => m.id === teamAssistId);
    if (idx < 0) {
      callback(null, { status: 404, statusText: statusText(404) });
      return;
    }

    session.messages[idx] = { ...session.messages[idx], rating: rating || null };

    callback(null, { status: 200, statusText: statusText(200), response: session.messages[idx] });
  }

  clearSession(attrs, callback, url, { orgId, agentId, customerId }) {
    delete this._sessions[customerId];

    this._pubsub.publish(`v1/orgs/${orgId}/agents/${agentId}/customers/${customerId}/team-assists/event/delete`, {
      topicParams: { orgId, agentId, customerId },
    });

    callback(null, { status: 200, statusText: statusText(200), response: {} });
  }

  getConfiguration(attrs, callback, url, { orgId }) {
    callback(null, {
      status: 200,
      statusText: statusText(200),
      response: { cannedResponses: CANNED_RESPONSES },
    });
  }

  getRoutes() {
    return bindCallbacks(
      {
        '/api/v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists': {
          GET: this.fetchAll,
          POST: this.ask,
          DELETE: this.clearSession,
        },
        '/api/v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists/:teamAssistId': {
          PATCH: this.updateMessage,
        },
        '/api/v1/orgs/:orgId/agents/:agentId/customers/:customerId/team-assists/:messageId/retry': {
          POST: this.retry,
        },
        '/api/v1/orgs/:orgId/configuration/teamassist': {
          GET: this.getConfiguration,
        },
      },
      this
    );
  }

  _getOrCreateSession(customerId) {
    if (!this._sessions[customerId]) {
      this._sessions[customerId] = {
        messages: [],
        responseIndex: 0,
        sessionId: `ta-session-${v4()}`,
      };
    }
    return this._sessions[customerId];
  }
}
